/**
 * PrescriptionPrintHandwritten.jsx
 *
 * STORAGE STRATEGY: Vector-only.
 * This component receives rx.handwritten.pages (stroke arrays) from MongoDB
 * and renders them to an offscreen <canvas> on demand — purely client-side.
 * No image is ever saved to the database. The canvas is created, used for
 * display/print, then discarded. Memory is cleaned up via URL.revokeObjectURL.
 */
import { useEffect, useState } from "react";

export default function PrescriptionPrintHandwritten({ rx, children }) {

const settings = rx?.settings;


const [cachedPages, setCachedPages] = useState([]);

const DRAW_WIDTH = 900;
const DRAW_HEIGHT = 1275;
const PAGE_WIDTH = 794;
const PAGE_HEIGHT = 1123;

/* ===== VECTOR -> IMAGE ===== */
const renderVectorPageToImage = (strokes = []) => {

  if (!Array.isArray(strokes) || !strokes.length) return null;

  const canvas = document.createElement("canvas");
  canvas.width = PAGE_WIDTH;
  canvas.height = PAGE_HEIGHT;

  const ctx = canvas.getContext("2d");

  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.imageSmoothingEnabled = true;

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  const scaleX = PAGE_WIDTH / DRAW_WIDTH;
  const scaleY = PAGE_HEIGHT / DRAW_HEIGHT;

  ctx.scale(scaleX, scaleY);

  strokes.forEach(s => {

    if (!s?.points?.length) return;

    ctx.save();

    ctx.globalCompositeOperation =
      s.tool === "eraser"
        ? "destination-out"
        : "source-over";

    ctx.strokeStyle = s.color || "#111827";
    ctx.beginPath();

    for (let i = 0; i < s.points.length - 1; i++) {

      const p1 = s.points[i];
      const p2 = s.points[i + 1];

      const mx = (p1.x + p2.x) / 2;
      const my = (p1.y + p2.y) / 2;

      ctx.lineWidth =
 s.tool === "pen"
   ? s.width
   : (p2.w || s.width);

      if (i === 0) ctx.moveTo(p1.x, p1.y);
      ctx.quadraticCurveTo(p1.x, p1.y, mx, my);

    }

    ctx.stroke();
    ctx.restore();

  });

  return new Promise(resolve=>{
    canvas.toBlob(blob=>{
      if(!blob) return resolve(null);
      const url = URL.createObjectURL(blob);
      resolve(url);
    },"image/png",0.95);
  });

};


useEffect(()=>{

  if(!rx?.modeUsed?.includes("handwritten")) return;
  if(!rx?.handwritten?.pages) return;

  let cancelled = false;

  (async()=>{

    const builtPages = await Promise.all(
      rx.handwritten.pages
        .filter(p=>Array.isArray(p)&&p.length)
        .map(async p=>({
          image: await renderVectorPageToImage(p)
        }))
    );

    if(cancelled) return;
    if(!builtPages.length) return;

    setCachedPages(builtPages);

  })();

  return ()=>{ cancelled=true };

},[rx]);

useEffect(()=>{
  return ()=>{
    cachedPages.forEach(p=>{
      if(p.image) URL.revokeObjectURL(p.image);
    });
  };
},[cachedPages]);


if(!cachedPages.length) return null;

if (typeof children !== "function") {
  console.error("PrescriptionPrintHandwritten expects children as function");
  return null;
}
return children(cachedPages);

}